import { userAccountInfo } from 'helpers/user'
import { logInUser } from 'redux/appInfo/appInfoSlice'
import { AppThunkAction } from 'redux/store'
import authTokenAPI, { GetAuthTokenResponse } from 'services/authToken'
import { AuthTokenActionPayload } from 'redux/authToken/typings'
import { redirectTo } from 'ui/customHooks/useHistory'
import {
  getDemographicChildInfo,
  getDemographicInfo
} from 'redux/demographic/async-thunks'
import {
  fetchAuthTokenFailed,
  selectSessionIdFromLogin,
  updateLoginInfoState
} from 'redux/loginInfo/loginInfoSlice'
import { setActiveAccount } from 'redux/demographic/demographicSlice'
import { createAsyncThunk } from '@reduxjs/toolkit'
import routePaths from 'const/routePaths'
import authCodeAPI from 'services/authCode'
import { OriginalRootState } from 'redux/rootReducer'
import {
  fetchAuthTokenSuccess,
  selectSessionIdFromEligibility
} from 'redux/resetPassword/resetPasswordSlice'
import {
  loginError,
  tokenRequestSuccess,
  updateAuthToken
} from './authTokenSlice'
import { ResetPasswordFlowProps, SwitchAccountFlowProps } from './typings'

const switchLinkedAccountFlow =
  ({
    accountNumber,
    accountID,
    tokenInfo,
    isProx,
    accountIndicator
  }: SwitchAccountFlowProps): AppThunkAction =>
  dispatch => {
    userAccountInfo.accountDetail = {
      'cipher.accountId': accountID
    }
    const { access_token: accessToken } = tokenInfo

    if (isProx) {
      if (accountIndicator?.toUpperCase() === 'P') {
        dispatch(
          getDemographicChildInfo({
            accountNumber,
            accessToken,
            accountID
          })
        )
      } else {
        dispatch(
          getDemographicInfo({
            accountNumber,
            accessToken,
            accountID
          })
        )
      }
    }

    dispatch(updateLoginInfoState({ isSwitchLinkedAccountFlow: false }))

    setTimeout(() => {
      redirectTo(routePaths.summary)
    }, 100)
  }

const switchAccountFlow =
  (accountID: string): AppThunkAction =>
  dispatch => {
    userAccountInfo.accountDetail = {
      'cipher.accountId': accountID
    }
    dispatch(updateLoginInfoState({ isSwitchAccountFlow: false }))

    setTimeout(() => {
      redirectTo(routePaths.summary)
    }, 100)
  }

const resetPasswordFlow =
  ({
    tokenInfo,
    isProx,
    accountNumber,
    accountID
  }: ResetPasswordFlowProps): AppThunkAction =>
  dispatch => {
    const { access_token: accessToken } = tokenInfo
    if (isProx) {
      dispatch(
        getDemographicInfo({
          accountNumber,
          accessToken,
          isResetPasswordFlow: true,
          accountID
        })
      )
    }
  }

const getAuthTokenFulfilled =
  (
    tokenInfo: GetAuthTokenResponse,
    action: AuthTokenActionPayload
  ): AppThunkAction =>
  // eslint-disable-next-line sonarjs/cognitive-complexity
  dispatch => {
    const {
      accountNumber,
      accountID,
      isResetPasswordFlow,
      isSwitchAccountFlow,
      accountIndicator,
      isSwitchLinkedAccountFlow
    } = action

    const { isProx } = userAccountInfo

    // what causes this prox condition to exists?
    const isAccountAvailable = isProx ? accountID && accountNumber : accountID

    dispatch(updateAuthToken({ tokenInfo }))

    if (isAccountAvailable) {
      dispatch(
        setActiveAccount({
          accountNumber,
          accountID
        })
      )

      if (isSwitchLinkedAccountFlow) {
        dispatch(
          switchLinkedAccountFlow({
            accountNumber,
            accountID,
            tokenInfo,
            isProx,
            accountIndicator
          })
        )
        return
      }

      if (isSwitchAccountFlow) {
        dispatch(switchAccountFlow(accountID))
        return
      }
      return
    }

    if (isResetPasswordFlow) {
      dispatch(
        resetPasswordFlow({
          tokenInfo,
          isProx,
          accountNumber,
          accountID
        })
      )
    }
  }

export const getAuthToken = createAsyncThunk(
  'authTokenSlice/getAuthToken',
  async (payload: AuthTokenActionPayload, { dispatch }) => {
    try {
      const response = await authTokenAPI.getAuthToken(payload)
      dispatch(getAuthTokenFulfilled(response, payload))
    } catch (error) {
      setTimeout(() => {
        redirectTo(routePaths.error)
      }, 200)

      dispatch(
        updateAuthToken({
          tokenInfo: null
        })
      )
    }
  }
)

/**
 * fetchAuthToken is required to be called from Login flow as well as Reset Password flow
 * This type is created to specify those flows so that respective actions can be dispatched
 */
export type FetchAuthTokenFlow = 'login' | 'resetpassword'

interface LoginTransmitSuccessPayload {
  /** token received from the Transmit success response */
  jwtToken: string
  /** Module from where the fetchAuthToken is being called */
  flow: FetchAuthTokenFlow
}

/**
 * Fetches the auth code and subsequent auth token following Transmit success
 */
export const fetchAuthToken = createAsyncThunk<
  GetAuthTokenResponse,
  LoginTransmitSuccessPayload,
  { state: OriginalRootState }
>(
  'authTokenSlice/fetchAuthToken',
  async ({ jwtToken, flow }, { getState, dispatch, rejectWithValue }) => {
    try {
      // Get sessionId based on the flow from where fetchAuthToken is dispatched
      const selectSessionId =
        flow === 'login'
          ? selectSessionIdFromLogin
          : selectSessionIdFromEligibility

      // Get action to be dispatched on success response based on the flow from where fetchAuthToken is dispatched
      const onFetchAuthTokenSuccess =
        flow === 'login' ? logInUser : fetchAuthTokenSuccess

      const sessionId = selectSessionId(getState())
      const { authCode } = await authCodeAPI.getAuthCode({
        jwtToken,
        sessionId
      })

      const tokenInfo = await authTokenAPI.getV3AuthToken({
        authCode
      })

      dispatch(tokenRequestSuccess(tokenInfo))
      dispatch(onFetchAuthTokenSuccess())
      return tokenInfo
    } catch (error) {
      dispatch(loginError())
      dispatch(fetchAuthTokenFailed())
      return rejectWithValue(error)
    }
  }
)
